// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { LanguagePair } from '../../../utils/arcRows';
import formatNumber from '../../../utils/formatNumber';
import { describeError as describeApiError } from '../../../utils/serverErrors';
// eslint-disable-next-line no-unused-vars
import { ApiError } from '../../../utils/serverErrors';
// eslint-disable-next-line no-unused-vars
import { ChangeSetStatus, ChangeSetSummary } from '../PricingModels/pricingModels';

export type ChangeSetEntityType = 'pricingModel' | 'scopingModel';

/** A decimal exactly as Pricing computed it, e.g. "31.71"; formatted for display, never computed with. */
export type Decimal = string;

export interface CurrencyAmount {
  currency: string;
  // Rounded to the currency's minor units.
  amount: Decimal;
}

export interface CostEstimate {
  // Each band's words times its weight, to 2 decimal places.
  weightedWords: Decimal;
  ratePerWord: Decimal;
  cost: CurrencyAmount;
}

export type ComparisonDirection = 'higher' | 'lower' | 'same';

/** How one cost compares with another in the same currency, e.g. live vs golden. */
export interface CostComparison {
  // The first cost minus the second; negative when it is lower.
  change: CurrencyAmount;
  // Of the second cost, to 2 decimal places; null when the second cost is zero.
  percent: Decimal | null;
  direction: ComparisonDirection;
}

/** Each null when either cost is null or they differ in currency. */
export interface CostComparisons {
  liveVsGolden: CostComparison | null;
  proposedVsGolden: CostComparison | null;
  proposedVsLive: CostComparison | null;
}

export type CostNoteCode =
  | 'no_weight_scheme'
  | 'no_price_scheme'
  | 'no_rate_for_arc'
  | 'pricing_model_not_live'
  | 'scoping_model_not_live';

/** Why a cost was priced in a fallback way or could not be priced. */
export interface CostNote {
  code: CostNoteCode;
  message: string;
}

export interface ChangedModel {
  entityType: ChangeSetEntityType;
  // The change set's own, except in a TER-group change set, whose models can be any
  // organization's; null only in skipped models recorded before Pricing stored it.
  organizationId: string | null;
  id: string;
  name: string | null;
}

export interface GoldenImpactRow {
  // The change set's own, or for a TER-group change set that of the model using the group.
  organizationId: string;
  // The golden dataset the row is from; Pricing sets both, though its schema allows null.
  datasetId: string | null;
  datasetName: string | null;
  priceSchemeId: string;
  weightSchemeId: string;
  pricingModelName: string;
  scopingModelName: string;
  arc: LanguagePair;
  proposedModels: ChangeSetEntityType[];
  golden: CurrencyAmount;
  // Null with liveUnavailable when it cannot be costed.
  live: CostEstimate | null;
  liveUnavailable: CostNote | null;
  // Set when live was priced without a live weight scheme.
  liveNote: CostNote | null;
  // Null when it cannot be costed.
  proposed: CostEstimate | null;
  // Set when proposed was priced without a live weight scheme, or, with proposed null, when a model
  // is not live yet and the change set does not write it.
  proposedNote: CostNote | null;
  comparisons: CostComparisons;
}

/** Costs of one kind summed over the rows of a currency that have that cost. */
export interface CostSum {
  cost: Decimal;
  rowCount: number;
}

/** A total's comparison, summed over only the rows that have both of its costs. */
export interface PairedCostComparison extends CostComparison {
  rowCount: number;
}

/** Each null when no row of the currency has both of its costs. */
export interface PairedCostComparisons {
  liveVsGolden: PairedCostComparison | null;
  proposedVsGolden: PairedCostComparison | null;
  proposedVsLive: PairedCostComparison | null;
}

export interface CurrencyImpactTotal {
  currency: string;
  // Each sum is null when no cost of that kind is in the currency.
  golden: CostSum | null;
  live: CostSum | null;
  proposed: CostSum | null;
  comparisons: PairedCostComparisons;
}

export type SkipReasonCode =
  | 'pricing_model_missing'
  | 'scoping_model_missing'
  | 'price_scheme_missing'
  | 'weight_scheme_missing'
  | 'dataset_version_missing';

/** Why Pricing did not price a dataset's rows, e.g. a missing model; a model not live yet is never one. */
export interface SkipReason {
  code: SkipReasonCode;
  message: string;
}

/** A golden dataset using a changed model whose rows Pricing left out of the impact. */
export interface SkippedDataset {
  datasetId: string;
  datasetName: string;
  organizationId: string;
  pricingModelId: string;
  scopingModelId: string;
  rowCount: number;
  reasons: SkipReason[];
}

/** The golden-set cost impact of a change set, as the portal web service forwards it from Pricing. */
export interface ChangeSetImpact {
  changeSetId: string;
  organizationId: string;
  changedModels: ChangedModel[];
  // By dataset name, then each dataset's rows in upload order.
  rows: GoldenImpactRow[];
  // One per currency.
  totals: CurrencyImpactTotal[];
  // Ordered like the rows.
  skippedDatasets: SkippedDataset[];
}

const PENDING: ChangeSetStatus = 'pending';

const ACCESS_DENIED_MESSAGE = 'You do not have permission to view the cost impact of change sets.';

const MODEL_LABELS: Record<ChangeSetEntityType, string> = {
  pricingModel: 'Pricing model',
  scopingModel: 'Scoping model',
};

export const changeSetImpactPath = (changeSetId: string): string =>
  `/changeSets/${encodeURIComponent(changeSetId)}/impact`;

/** The newest pending change set of a history listed newest first, or null when none is pending. */
export const findLatestPendingChangeSetId = (changeSets: ChangeSetSummary[]): string | null => {
  const pending = changeSets.find(changeSet => changeSet.status === PENDING);
  return pending ? pending.changeSetId : null;
};

export const describeError = (error: ApiError): string[] =>
  describeApiError(error, ACCESS_DENIED_MESSAGE);

const ROW_COUNT = 'rowCount';

const withDecimalStrings = (value: unknown): unknown => {
  if (typeof value === 'number') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(withDecimalStrings);
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as object).map(([key, field]) => [
        key,
        key === ROW_COUNT ? field : withDecimalStrings(field),
      ])
    );
  }
  return value;
};

/**
 * Pricing's impact response with its decimals as strings. Pricing sends them as JSON numbers, and
 * every number in the response other than a row count is such a decimal.
 */
export const toChangeSetImpact = (response: unknown): ChangeSetImpact =>
  withDecimalStrings(response) as ChangeSetImpact;

/** An amount in its currency's own decimal places, e.g. €31.71 or ¥3,171. */
export const formatAmount = (amount: Decimal, currency: string): string =>
  formatNumber(Number(amount), { style: 'currency', currency });

/** Shown in place of a cost or comparison Pricing could not compute. */
export const NOT_AVAILABLE = 'Not available';

const DIRECTION_MARKS: Record<
  Exclude<ComparisonDirection, 'same'>,
  { arrow: string; word: string; percentSign: string }
> = {
  higher: { arrow: '▲', word: 'higher', percentSign: '+' },
  lower: { arrow: '▼', word: 'lower', percentSign: '−' },
};

const unsigned = (value: Decimal): Decimal => value.replace(/^-/, '');

/**
 * A comparison worded by its direction, e.g. "▲ €1.28 higher (+4.36%)", "▼ €2.38 lower (−7.51%)" or
 * "— same". The word carries the amount's sign; the percentage is signed by the direction.
 */
export const formatComparison = ({ change, percent, direction }: CostComparison): string => {
  if (direction === 'same') {
    return '— same';
  }
  const { arrow, word, percentSign } = DIRECTION_MARKS[direction];
  const amountText = `${formatAmount(unsigned(change.amount), change.currency)} ${word}`;
  if (percent === null) {
    return `${arrow} ${amountText}`;
  }
  const percentText = formatNumber(Number(unsigned(percent)), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${arrow} ${amountText} (${percentSign}${percentText}%)`;
};

export const comparisonText = (comparison: CostComparison | null): string =>
  comparison ? formatComparison(comparison) : NOT_AVAILABLE;

const rowsText = (rowCount: number): string => (rowCount === 1 ? '1 row' : `${rowCount} rows`);

/** A sum with the number of rows it adds, e.g. "€29.33 (1 row)". */
export const formatCostSum = ({ cost, rowCount }: CostSum, currency: string): string =>
  `${formatAmount(cost, currency)} (${rowsText(rowCount)})`;

/** The rows a total's comparison covers, e.g. "over 2 rows". */
export const formatComparedRows = (rowCount: number): string => `over ${rowsText(rowCount)}`;

export const formatWeightedWords = (weightedWords: Decimal): string =>
  `${formatNumber(Number(weightedWords), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} weighted words`;

export const formatProposedModels = (proposedModels: ChangeSetEntityType[]): string =>
  proposedModels.map(model => MODEL_LABELS[model]).join(', ');
